import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  useGetAttachmentsByDocId,
  type Attachment,
} from "@/api/attachment";
import {
  fetchCatalogList,
  type CatalogPickItem,
} from "@/api/cms-puck/catalog-picker-api";
import {
  attachmentLargeThumb,
  catalogImageLabel,
  catalogMediaKindBadge,
  filterCatalogMedia,
  type CatalogMediaKind,
} from "@/lib/cms-puck/media-utils";
import {
  getCatalogItemTypes,
  type SelectionItemType,
} from "@/lib/cms-puck/blocks/selection";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/searchbar";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";

/**
 * Picker en 2 étapes pour choisir un média parmi ceux d'un élément du BO :
 * 1. Recherche d'élément (toutes tables catalogue, comme ItemSpotlight)
 * 2. Grille des médias de l'élément sélectionné
 *
 * Mode référence directe : retourne l'`attachmentId` BO existant (pas de copie).
 */

type SelectedElement = CatalogPickItem & { type: SelectionItemType };

const ALL_CATALOG_MEDIA_KINDS: CatalogMediaKind[] = ["image", "video", "embed"];

export type BoElementImagePickerBodyProps = {
  /** Sélection simple : retourne l'id et ferme (comportement MediaPicker). */
  onPick?: (attachmentId: string) => void;
  /** Mode multi-sélection (GalleryPicker) : toggle sans fermer. */
  multiSelect?: boolean;
  selectedIds?: Set<string>;
  onToggle?: (id: string) => void;
  /** Contexte médiathèque : affiche un panneau détail au lieu de sélectionner. */
  browseOnly?: boolean;
  onBrowseSelect?: (payload: {
    attachment: Attachment;
    element: SelectedElement;
  }) => void;
  allowedTypes?: SelectionItemType[];
  /** Kinds autorisés à l'étape 2 (défaut : image + vidéo + embed). */
  allowedKinds?: CatalogMediaKind[];
};

function Thumb({
  url,
  alt,
  size = 96,
  kindBadge,
}: {
  url?: string;
  alt?: string;
  size?: number;
  kindBadge?: string;
}) {
  if (!url) {
    return (
      <div
        className="relative flex items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400"
        style={{ width: size, height: size }}
      >
        {kindBadge || "—"}
      </div>
    );
  }
  return (
    <div
      className="relative flex items-center justify-center overflow-hidden rounded bg-slate-100"
      style={{ width: size, height: size }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt={alt ?? ""}
        className="max-h-full max-w-full object-contain"
      />
      {kindBadge ? (
        <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 py-0.5 text-[9px] font-medium text-white">
          {kindBadge}
        </span>
      ) : null}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function ElementSearchStep({
  allowedTypes,
  onSelectElement,
}: {
  allowedTypes?: SelectionItemType[];
  onSelectElement: (element: SelectedElement) => void;
}) {
  const cmsNew = useCmsNew();
  const types = allowedTypes?.length
    ? getCatalogItemTypes().filter((t) => allowedTypes.includes(t.type))
    : getCatalogItemTypes();
  const [type, setType] = useState<SelectionItemType>(
    () => types[0]?.type ?? "artwork",
  );
  const [search, setSearch] = useState("");

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["catalog-picker", type, search.trim()],
    queryFn: () => fetchCatalogList(type, search),
    // Garde la grille pendant une recherche, pas lors d'un changement d'onglet
    // (sinon on affiche les vignettes du type précédent sous le mauvais label).
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery?.queryKey?.[1] === type) return previousData;
      return undefined;
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        {types.map((t) => (
          <TabButton
            key={t.type}
            active={type === t.type}
            onClick={() => setType(t.type)}
          >
            {t.label}
          </TabButton>
        ))}
      </div>
      <SearchBar
        search={search}
        onChangeSearch={setSearch}
        placeholder={cmsNew("cms.new.auto.placeholder.rechercher_un_element", "Rechercher un élément…")}
      />
      {isLoading ? (
        <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
      ) : results.length === 0 ? (
        <p className="text-sm text-slate-500">
          {search.trim()
            ? cmsPhrase(cmsNew, "pickerNoResults")
            : cmsPhrase(cmsNew, "pickerNoItems")}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {results.map(({ id, title, registrationNumber, url }) => {
            const altText = [registrationNumber, title]
              .filter(Boolean)
              .join(" — ");
            return (
              <button
                key={id}
                type="button"
                onClick={() =>
                  onSelectElement({
                    type,
                    id,
                    title,
                    registrationNumber,
                    url,
                  })
                }
                className="group flex flex-col items-center gap-0.5 rounded border border-transparent p-1 hover:border-slate-300"
                title={altText}
              >
                <Thumb url={url} alt={altText} size={96} />
                {registrationNumber ? (
                  <span className="line-clamp-1 w-full text-center text-[10px] font-medium text-slate-600">
                    {registrationNumber}
                  </span>
                ) : null}
                {title ? (
                  <span className="line-clamp-1 w-full text-center text-[11px] text-slate-500">
                    {title}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MediaPickStep({
  element,
  allowedKinds,
  multiSelect,
  selectedIds,
  browseOnly,
  onBack,
  onPick,
  onToggle,
  onBrowseSelect,
}: {
  element: SelectedElement;
  allowedKinds: CatalogMediaKind[];
  multiSelect?: boolean;
  selectedIds?: Set<string>;
  browseOnly?: boolean;
  onBack: () => void;
  onPick?: (attachmentId: string) => void;
  onToggle?: (id: string) => void;
  onBrowseSelect?: BoElementImagePickerBodyProps["onBrowseSelect"];
}) {
  const cmsNew = useCmsNew();
  const { data: attachments = [], isLoading } = useGetAttachmentsByDocId(
    element.id,
  );
  const media = useMemo(
    () => filterCatalogMedia(attachments, allowedKinds),
    [attachments, allowedKinds],
  );

  const typeLabel =
    getCatalogItemTypes().find((t) => t.type === element.type)?.label ??
    element.type;
  const elementLabel = [element.registrationNumber, element.title]
    .filter(Boolean)
    .join(" — ");

  const handleMediaClick = (att: Attachment) => {
    if (browseOnly && onBrowseSelect) {
      onBrowseSelect({ attachment: att, element });
      return;
    }
    if (multiSelect && onToggle) {
      onToggle(att.id);
      return;
    }
    onPick?.(att.id);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="h-8 px-2"
        >
          <ArrowLeft size={14} className="mr-1" />
          {cmsPhrase(cmsNew, "back")}
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-700">
            {elementLabel || element.id.slice(0, 8)}
          </p>
          <p className="text-[11px] text-slate-400">{typeLabel}</p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement_des_medias", "Chargement des médias…")}</p>
      ) : media.length === 0 ? (
        <p className="text-sm text-slate-500">{cmsPhrase(cmsNew, "mediaNoElementMedia")}</p>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {media.map((att) => {
            const active = selectedIds?.has(att.id) ?? false;
            const label = catalogImageLabel(att);
            const kindBadge = catalogMediaKindBadge(att.kind);
            return (
              <button
                key={att.id}
                type="button"
                onClick={() => handleMediaClick(att)}
                className={`group flex flex-col items-center gap-1 rounded border p-1 ${
                  active
                    ? "border-primary ring-2 ring-primary/40"
                    : "border-transparent hover:border-slate-300"
                }`}
                title={label}
              >
                <Thumb
                  url={attachmentLargeThumb(att)}
                  alt={label}
                  size={96}
                  kindBadge={kindBadge}
                />
                <span className="line-clamp-2 w-full text-center text-[10px] text-slate-500">
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function BoElementImagePickerBody({
  onPick,
  multiSelect,
  selectedIds,
  onToggle,
  browseOnly,
  onBrowseSelect,
  allowedTypes,
  allowedKinds = ALL_CATALOG_MEDIA_KINDS,
}: BoElementImagePickerBodyProps) {
  const [element, setElement] = useState<SelectedElement | null>(null);

  if (element) {
    return (
      <MediaPickStep
        element={element}
        allowedKinds={allowedKinds}
        multiSelect={multiSelect}
        selectedIds={selectedIds}
        browseOnly={browseOnly}
        onBack={() => setElement(null)}
        onPick={onPick}
        onToggle={onToggle}
        onBrowseSelect={onBrowseSelect}
      />
    );
  }

  return (
    <ElementSearchStep
      allowedTypes={allowedTypes}
      onSelectElement={setElement}
    />
  );
}
